import { Check, ChevronLeft, ChevronRight } from 'lucide-react';

const PlanCards = ({ 
  selectedCard, 
  setSelectedCard, 
  selectedPlatforms, 
  setSelectedPlatforms, 
  handlePlatformSelect,
  planSlideIndex,
  setPlanSlideIndex 
}) => {
  const plans = [
    {
      id: 'instant',
      name: 'NEXXO INSTANT',
      features: ['Get funded instantly – no evaluations, no delays', 'The fastest route to trading capital', 'Designed for confident and experienced traders'],
      price: '$20',
      account: '$1250',
      platforms: ['MT5','TradeLocker']
    },
    {
      id: 'prime',
      name: 'NEXXO PRIME',
      features: ['No consistency rule – flexible trading freedom', 'Built for long-term evaluation success', 'Choose between 2-step or 3-step evaluations'],
      price: '$240',
      account: '$100K',
      platforms: ['MT5','TradeLocker','cTrader']
    },
    {
      id: 'power',
      name: 'NEXXO POWER',
      features: ['Best value with the most affordable pricing', 'Ideal starting point for new traders', 'Straightforward 2-step evaluation process'],
      price: '$35',
      account: 'Various',
      platforms: ['MT5','TradeLocker']
    }
  ];

  // Mobile/Tablet Carousel Plan Cards
  const MobilePlanCards = () => {
    const current = plans[planSlideIndex % plans.length];
    const goPrev = () => setPlanSlideIndex((i) => (i - 1 + plans.length) % plans.length);
    const goNext = () => setPlanSlideIndex((i) => (i + 1) % plans.length);
    
    return (
      <div className="relative">
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border-2 shadow-xl transition-all duration-300 cursor-pointer relative mx-6"
             onClick={() => {
               const newSelectedCard = selectedCard === current.id ? null : current.id;
               setSelectedCard(newSelectedCard);
               if (newSelectedCard !== current.id) {
                 setSelectedPlatforms(prev => ({ ...prev, [current.id]: undefined }));
               }
             }}
        >
          {selectedCard === current.id && (
            <div className="absolute top-4 right-4 bg-cyan-400 text-gray-900 rounded-full p-2">
              <Check size={16} className="font-bold" />
            </div>
          )}
          <h3 className="text-xl font-bold mb-4">{current.name}</h3>
          <ul className="space-y-2 text-sm text-gray-300 mb-6">
            {current.features.map((f, i) => (
              <li key={i} className="flex items-start gap-2">
                <Check size={16} className="text-cyan-400 mt-0.5" />
                {f}
              </li>
            ))}
          </ul>
          <div className="bg-gray-900 rounded-xl p-4">
            <p className={`text-sm font-semibold mb-3 ${selectedCard === current.id ? 'text-white' : 'text-gray-500'}`}>Choose your platform:</p>
            {selectedCard !== current.id && (
              <div className="mb-3 text-center">
                <span className="text-xs text-gray-500 bg-gray-800 px-3 py-1 rounded-full">Select this plan first to choose platform</span>
              </div>
            )}
            <div className="flex gap-3 flex-wrap">
              {current.platforms.map((plat) => (
                <button key={plat} onClick={(e) => { e.stopPropagation(); if (selectedCard === current.id) { handlePlatformSelect(current.id, plat); } }} disabled={selectedCard !== current.id}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-300 ${selectedCard !== current.id ? 'bg-gray-700 text-gray-500 cursor-not-allowed opacity-50' : selectedPlatforms[current.id] === plat ? 'bg-cyan-400 text-gray-900 shadow-lg ring-2 ring-cyan-400/50' : 'bg-gray-800 hover:bg-gray-700 text-white cursor-pointer'}`}>
                  <img src={`/${plat.toLowerCase()}.png`} alt={plat} className={`w-8 h-8 ${selectedCard !== current.id ? 'opacity-50 grayscale' : ''}`} />
                  <span className="font-medium">{plat}</span>
                  {selectedPlatforms[current.id] === plat && selectedCard === current.id && (<Check size={16} className="ml-1" />)}
                </button>
              ))}
            </div>
          </div>
        </div>
        {/* Controls - overlay icons left/right */}
        <button onClick={goPrev} aria-label="Previous" className="absolute top-1/2 -translate-y-1/2 -left-3 z-10 p-2 rounded-full bg-gray-900/70 border border-gray-700 text-gray-200 shadow hover:bg-gray-800">
          <ChevronLeft size={18} />
        </button>
        <button onClick={goNext} aria-label="Next" className="absolute top-1/2 -translate-y-1/2 -right-3 z-10 p-2 rounded-full bg-gray-900/70 border border-gray-700 text-gray-200 shadow hover:bg-gray-800">
          <ChevronRight size={18} />
        </button>
      </div>
    );
  };

  // Desktop Grid Plan Cards
  const DesktopPlanCards = () => (
    <div className="hidden lg:grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
      {plans.map((plan) => (
        <div 
          key={plan.name}
          onClick={() => {
            const newSelectedCard = selectedCard === plan.id ? null : plan.id;
            setSelectedCard(newSelectedCard);
            if (newSelectedCard !== plan.id) {
              setSelectedPlatforms(prev => ({ ...prev, [plan.id]: undefined }));
            }
          }}
          className={`bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border-2 shadow-xl hover:scale-[1.02] transition-all duration-300 cursor-pointer relative ${
            selectedCard === plan.id ? 'border-cyan-400 shadow-2xl shadow-cyan-400/20 ring-2 ring-cyan-400/50' : 'border-gray-700 hover:border-gray-600'
          }`}
        >
          {selectedCard === plan.id && (
            <div className="absolute top-4 right-4 bg-cyan-400 text-gray-900 rounded-full p-2">
              <Check size={16} className="font-bold" />
            </div>
          )}
          <h3 className="text-xl font-bold mb-4">{plan.name}</h3>
          <ul className="space-y-2 text-sm text-gray-300 mb-6">
            {plan.features.map((f, i) => (
              <li key={i} className="flex items-start gap-2">
                <Check size={16} className="text-cyan-400 mt-0.5" />
                {f}
              </li>
            ))}
          </ul>
          <div className="bg-gray-900 rounded-xl p-4">
            <p className={`text-sm font-semibold mb-3 ${selectedCard === plan.id ? 'text-white' : 'text-gray-500'}`}>Choose your platform:</p>
            {selectedCard !== plan.id && (
              <div className="mb-3 text-center">
                <span className="text-xs text-gray-500 bg-gray-800 px-3 py-1 rounded-full">Select this plan first to choose platform</span>
              </div>
            )}
            <div className="flex gap-3 flex-wrap">
              {plan.platforms.map((plat) => (
                <button
                  key={plat}
                  onClick={(e) => { e.stopPropagation(); if (selectedCard === plan.id) { handlePlatformSelect(plan.id, plat); } }}
                  disabled={selectedCard !== plan.id}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-300 ${
                    selectedCard !== plan.id
                      ? 'bg-gray-700 text-gray-500 cursor-not-allowed opacity-50'
                      : selectedPlatforms[plan.id] === plat
                      ? 'bg-cyan-400 text-gray-900 shadow-lg ring-2 ring-cyan-400/50'
                      : 'bg-gray-800 hover:bg-gray-700 text-white cursor-pointer'
                  }`}
                >
                  <img src={`/${plat.toLowerCase()}.png`} alt={plat} className={`w-8 h-8 ${selectedCard !== plan.id ? 'opacity-50 grayscale' : ''}`} />
                  <span className="font-medium">{plat}</span>
                  {selectedPlatforms[plan.id] === plat && selectedCard === plan.id && (<Check size={16} className="ml-1" />)}
                </button>
              ))}
            </div>
            {selectedPlatforms[plan.id] && selectedCard === plan.id && (
              <div className="mt-3 text-center">
                <span className="inline-flex items-center gap-2 px-3 py-1 bg-cyan-400/20 text-cyan-400 rounded-full text-xs font-medium"><Check size={12} />{selectedPlatforms[plan.id]} Selected</span>
              </div>
            )}
          </div>
          {selectedCard === plan.id && (
            <div className="mt-4 text-center">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-400 text-gray-900 rounded-full text-sm font-bold"><Check size={14} />Selected Plan</span>
            </div>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <>
      {/* Plan Cards - Mobile/Tablet Carousel */}
      <div id="plans" className="lg:hidden mb-12">
        <MobilePlanCards />
      </div>

      {/* Plan Cards - Desktop Grid */}
      <DesktopPlanCards />
    </>
  );
};

export default PlanCards;
